import React from 'react';
import { Typography } from '@material-ui/core';
import { useTranslate } from 'react-admin';
import { time } from '~/utils';
import { AvatarField } from '~/components/ra';
import { ChangelogModeNotice, ViewHistoryButton } from '~/components/Changelog';
import DraftModeNotice from '~/components/Drafts/DraftModeNotice';
import { getName, getTypeOfIdentity } from '~/utils/identities';
import { useReferenceEntity } from '~/hooks';
import { useEditMode } from '~/hooks/useEditModeContext';
import { removeDraftPostfix } from '~/hooks/draft';
import CopyButton from '~/components/CopyButton';
import { Business, Individual } from '~/types';
import { useRecordTitleStyles, RecordTitleClasses } from './styles';
import { l, RecordTitleProps } from './types';

const defaultSubtitle = (
  record: Individual | Business,
  resource: string,
  translate: any,
  classes: RecordTitleClasses
) => {
  return (
    <>
      {record.id && (
        <span className={classes.subtitleUuidRow}>
          {`${translate(`resources.${resource}.name`, 1)} `}
          <span className={classes.subtitleUuid}>{removeDraftPostfix(record.id)}</span>
          <CopyButton
            value={removeDraftPostfix(record.id)}
            classes={{ button: classes.subtitleCopyButton, icon: classes.subtitleCopyIcon }}
          />
        </span>
      )}
      {record.id && record.createdAt && ', '}
      {record.createdAt && `${translate(l('createdOn'))} ${dateFormat(record.createdAt)}.`}{' '}
    </>
  );
};

const ProfileData = (props: RecordTitleProps) => {
  const { record, resource, avatarSource, headline, translate, hasPathnameProfile } = props;

  const classes = useRecordTitleStyles();

  const { editMode } = useEditMode();

  const lastUpdatedById = record?.lastUpdatedBy?.id;
  const lastUpdatedByType: string = getTypeOfIdentity(record?.lastUpdatedBy?.type ?? 'SYSTEM');
  const { data: lastUpdatedBy, isLoading: isLoadingLastUpdatedBy } = useReferenceEntity(
    { id: lastUpdatedById!, type: lastUpdatedByType },
    !!lastUpdatedById
  );
  const originatorName = getName?.[lastUpdatedByType]?.(lastUpdatedBy ?? {}) ?? lastUpdatedById;

  const translateLabel = useTranslate();
  const t = (key: string, arg?: any): string => translateLabel(l(key), arg);

  return (
    <div className={classes.root}>
      <div className={classes.avatar}>
        <AvatarField
          record={record}
          source={avatarSource}
          resource={resource}
          noText={true}
          size='50px'
        />
      </div>
      <div>
        {headline ? (
          <Typography variant='h2' className={classes.headline}>
            {headline(record, resource, translate)}
          </Typography>
        ) : null}
        <Typography variant='subtitle1' className={classes.subtitle}>
          {defaultSubtitle(record, resource, translate, classes)}
          {hasPathnameProfile && editMode === 'VIEW' && (
            <>
              {t('lastUpdatedBy', {
                date: time(record.lastUpdatedAt).format(dateMomentFormat),
                time: time(record.lastUpdatedAt).format(timeMomentFormat),
                by: isLoadingLastUpdatedBy ? t('loading') : originatorName,
              })}
              <ViewHistoryButton />
            </>
          )}
          {hasPathnameProfile && editMode === 'CHANGELOG' && <ChangelogModeNotice />}
          {hasPathnameProfile && editMode === 'DRAFT' && <DraftModeNotice />}
        </Typography>
      </div>
    </div>
  );
};

const dateMomentFormat = 'l';
const timeMomentFormat = 'LT';
const dateFormat = (dateString: string) => {
  return `${time(dateString).format(dateMomentFormat)} ${time(dateString).format(
    timeMomentFormat
  )}`;
};

export default ProfileData;
